/**
 * Per-task dispatch progress and resend (spec 7.3, 8.4) — Phase 2C, Task 14.
 *
 * A sibling file rather than more keys in `reviewRequests.composer.ts`, for
 * the reason `reviewRequests.confirm.ts` and its siblings exist: that file
 * was 374 lines against this repo's 400-line hard limit before this task's
 * eight reason sentences, five step names, five state words and one manual
 * plural pair. The whole `dispatch` object moved here and is spread back into
 * the SAME `reviewRequests.requests` namespace, so every key below is still
 * `reviewRequests.requests.dispatch.*` and no import path anywhere changed.
 *
 * Same two constraints as its siblings: English only, and `{named}`
 * placeholders only — `cspSafeMessageCompiler` cannot parse vue-i18n's `|`
 * plural syntax, so count-dependent text is a manual `_one` / `_other` pair.
 *
 * Phase 2C drains the outbox, so these are statements about work that is
 * actually happening. 2B's "Queued" was deliberate — it drained nothing — and
 * `queued: 'Sending'` below is the revision that phase's own comment asked
 * for. Kept byte-identical to `list.status.dispatching`
 * (`reviewRequests.list.ts`): two screens wording one fact differently is
 * what that comment warned about.
 */
export const reviewRequestsDispatchMessages = {
  dispatch: {
    queued: "Sending",
    queuedHelp:
      "This request has been accepted and its documents are being delivered.",
    sent: "Sent",
    failed: "Failed",

    // Manual singular/plural pairs: `cspSafeMessageCompiler` does {named}
    // substitution only and cannot parse vue-i18n's `|` syntax.
    progress_one: "{succeeded} of {total} step complete",
    progress_other: "{succeeded} of {total} steps complete",
    // F1: delivery steps a revoke or a cancel stopped on purpose. Neutral,
    // and outside the "N of M" above, which counts only what is still going
    // out.
    stopped_one: "{count} step not sent — access revoked or request cancelled",
    stopped_other:
      "{count} steps not sent — access revoked or request cancelled",
    // A dead access withdrawal: the scholar's SharePoint access was meant to
    // be removed and was not. Its row, with a Resend button, is among the
    // request-level rows directly below the strip.
    withdrawalAttention_one:
      "{count} access withdrawal failed — resend it below",
    withdrawalAttention_other:
      "{count} access withdrawals failed — resend them below",

    state: {
      pending: "Waiting",
      in_flight: "In progress",
      succeeded: "Done",
      failed: "Retrying",
      dead: "Needs attention",
      // F1: a step a revoke or a cancel stopped on purpose. Not a failure.
      stopped: "Not sent",
    },

    // One label per `task_type` the backend can send
    // (`reviewRequests.dispatch.types.ts`'s union, mirroring
    // `dispatch_plan.rs`), plus the two senses of `create_folder` that
    // `dispatchRules.ts::taskTypeKey` tells apart by the null scholar id.
    // A type with no label here renders its raw i18n key: `i18n.ts` sets
    // neither `missingWarn` nor a fallback, so vue-i18n prints the key
    // string into the Delivery block. Adding a type to the union means
    // adding its label in the SAME change.
    step: {
      review_folder: "Review folder",
      scholar_folder: "Scholar folder",
      copy_document: "Document copy",
      create_link: "Review link",
      send_email: "Email to the scholar",
      raise_po: "Purchase order",
      // Not a delivery step: this is the withdrawal of a SharePoint
      // permission after a link was revoked or the request cancelled. It
      // renders among the request-level rows (its scholar id is null) so
      // that a withdrawal which stops responding can still be resent, but
      // the backend leaves it out of the progress counters.
      delete_permission: "Access withdrawal",
    },

    // Ruling 13's closed set. Every code the backend can send has a sentence
    // here; adding a code to `dto/dispatch.rs` means adding one here in the
    // same change, or the row renders the `upstream` fallback.
    reason: {
      permission_denied:
        "SharePoint refused this step. The site permission Suhayl uses may have changed.",
      not_found: "The SharePoint item this step needed no longer exists.",
      moved:
        "This document is no longer in the folder the request was composed from. It has been moved or replaced since the request was sent.",
      sharing_disabled:
        "This SharePoint site does not allow the private links Suhayl sends to scholars.",
      no_email:
        "This scholar has no email address on record. Add one, then resend.",
      odoo_unconfigured:
        "The purchase-order gateway is not connected yet. Resend this step once it is.",
      blocked:
        "An earlier step for this scholar did not finish, so this one was not attempted.",
      // Unreachable through `create_link` today (Ruling 11: it makes no
      // network call), and the live per-open mint this can come from refuses
      // and withdraws the grant rather than dead-lettering an outbox task —
      // see the module's CLAUDE.md, "Known follow-up debt". Kept here so the
      // day a dispatch task does reach `create_anonymous_link`, its dead
      // letter renders this sentence instead of the generic `upstream` one.
      grant_downgraded:
        "SharePoint granted a narrower permission than this step needed. The site's sharing settings may need adjusting.",
      // F1: deliberate stops, worded as facts rather than failures -- these
      // rows are not resendable and nothing needs fixing.
      revoked: "Not sent: this scholar's access was revoked.",
      cancelled: "Not sent: the request was cancelled.",
      upstream:
        "This step did not complete. Resend it, or contact support if it fails again.",
    },

    resend: "Resend",
    resending: "Resending…",
    // Task 14: the visible button text is the bare word "Resend" on every
    // row, which a screen reader announces identically for every step on the
    // page. The accessible name names the step instead, via `taskTypeKey`'s
    // own label (e.g. "Resend Review link").
    resendAria: "Resend {step}",
    // Fix round 1, Finding 1: `resendable` is true ONLY for `dead`
    // (`dto/dispatch.rs`'s own test asserts false for every other state), so
    // the tooltip on a non-resendable button must be chosen by `task.state`,
    // never by the bare negation of `resendable` — that read "already
    // completed" onto `pending`/`in_flight`/`failed` rows too, including the
    // very row `useDispatchResend.ts` had just re-queued to `pending`.
    resendDone: "This step has already completed, so it cannot be resent.",
    resendNotYet:
      "This step has not finished yet. It can only be resent if it stops responding.",
    // Task 12: a dead step `dto/dispatch.rs::resendable` refuses, mirroring
    // `handlers/resend.rs`.
    resendRefused:
      "This step cannot be resent: the request was cancelled, or this scholar's access was revoked.",
    resendFailed: "This step could not be resent. Please try again.",
    // F2: the resend never reached the server (a network failure), so
    // nothing was refused and the row can be tried again.
    resendUnreachable: "Could not reach the server. Please try again.",
  },
};
